import type React from 'react';
import classes from '../work-schedule-page.module.scss';
import { shiftIsoDate, getCurrentWeekStartIsoDate } from '../../../utils/date.utils';
import { WEEKDAY_FILTER_LABELS } from '../work-schedule-page.utils';
import type { useWorkSchedulePageController } from '../useWorkSchedulePageController';
import { getEmployeeLimitWarnings, EMPLOYEE_LIMIT_LABELS } from '../../../utils/employee-limits.utils';

type ScheduleSidebarProps = Pick<
  ReturnType<typeof useWorkSchedulePageController>,
  | 'weekRangeLabel'
  | 'setWeekStartDate'
  | 'isLoadingWeek'
  | 'canManageSchedule'
  | 'isPublishing'
  | 'onChangePublication'
  | 'onOpenCreateSectionModal'
  | 'filtersWrapRef'
  | 'hasActiveFilters'
  | 'isFiltersOpen'
  | 'setIsFiltersOpen'
  | 'activeFilterCount'
  | 'selectedDayOffsets'
  | 'toggleSetValue'
  | 'setSelectedDayOffsets'
  | 'shiftFilterOptions'
  | 'selectedShiftNames'
  | 'setSelectedShiftNames'
  | 'roleFilterOptions'
  | 'selectedRoleNames'
  | 'setSelectedRoleNames'
  | 'onSelectAllFilters'
  | 'onResetFilters'
  | 'setIsOptionsOpen'
  | 'isOptionsOpen'
  | 'refreshCurrentWeek'
  | 'onExportSchedule'
  | 'scheduleWeek'
  | 'user'
  | 'employeeInsightSummaries'
  | 'employeeInsight'
  | 'onOpenEmployeeInsight'
  | 'isLoadingEmployeeInsight'
>;

export const ScheduleSidebar: React.FC<ScheduleSidebarProps> = ({
  weekRangeLabel,
  setWeekStartDate,
  isLoadingWeek,
  canManageSchedule,
  isPublishing,
  onChangePublication,
  onOpenCreateSectionModal,
  filtersWrapRef,
  hasActiveFilters,
  isFiltersOpen,
  setIsFiltersOpen,
  activeFilterCount,
  selectedDayOffsets,
  toggleSetValue,
  setSelectedDayOffsets,
  shiftFilterOptions,
  selectedShiftNames,
  setSelectedShiftNames,
  roleFilterOptions,
  selectedRoleNames,
  setSelectedRoleNames,
  onSelectAllFilters,
  onResetFilters,
  setIsOptionsOpen,
  isOptionsOpen,
  refreshCurrentWeek,
  onExportSchedule,
  scheduleWeek,
  user,
  employeeInsightSummaries,
  employeeInsight,
  onOpenEmployeeInsight,
  isLoadingEmployeeInsight,
}) => (
  <aside className="contentCard workspaceSidebar scheduleSidebar">
    <div className="workspaceSidebarHeader">
      <h1 className="homeTitle">סידור עבודה</h1>
      <p className="homeHint">ניהול הסידור השבועי</p>
    </div>

    <p className="workspaceWeekLabel" aria-live="polite">
      {weekRangeLabel}
    </p>

    {scheduleWeek?.publication && (
      <p role="status">{!scheduleWeek.publication.isPublished ? user?.role === 'manager' ? 'טיוטה' : 'הסידור טרם פורסם'
        : user?.role === 'manager' && scheduleWeek.publication.publishedHash
          && scheduleWeek.publication.draftHash !== scheduleWeek.publication.publishedHash ? 'שינויים שלא פורסמו' : 'פורסם'}</p>
    )}
    {canManageSchedule && scheduleWeek?.publication && (
      <>
        <button type="button" className="button schedulePrimaryButton"
          disabled={isLoadingWeek || isPublishing || !scheduleWeek.publication.draftHash}
          onClick={() => void onChangePublication(true)}>
          {isPublishing ? 'מעדכן...' : scheduleWeek.publication.isPublished ? 'פרסום מחדש' : 'פרסום הסידור'}
        </button>
        {scheduleWeek.publication.isPublished && (
          <button type="button" className="button" disabled={isLoadingWeek || isPublishing}
            onClick={() => void onChangePublication(false)}>ביטול פרסום</button>
        )}
      </>
    )}

    <div className="workspaceWeekNav">
      <button
        type="button"
        className="navArrowButton"
        onClick={() => setWeekStartDate((current) => shiftIsoDate(current, -7))}
        disabled={isLoadingWeek}
      >
        שבוע קודם
      </button>
      <button
        type="button"
        className="navArrowButton"
        onClick={() => setWeekStartDate(getCurrentWeekStartIsoDate())}
        disabled={isLoadingWeek}
      >
        השבוע הנוכחי
      </button>
      <button
        type="button"
        className="navArrowButton"
        onClick={() => setWeekStartDate((current) => shiftIsoDate(current, 7))}
        disabled={isLoadingWeek}
      >
        שבוע הבא
      </button>
    </div>

    {canManageSchedule && (
      <button
        type="button"
        className="button schedulePrimaryButton"
        onClick={onOpenCreateSectionModal}
        disabled={isLoadingWeek}
      >
        + הוספת מחלקה
      </button>
    )}

    <div className={classes.scheduleFiltersWrap} ref={filtersWrapRef}>
      <button
        type="button"
        className={`${classes.scheduleFiltersButton}${hasActiveFilters ? ' active' : ''}`}
        aria-expanded={isFiltersOpen}
        onClick={() => setIsFiltersOpen((current) => !current)}
      >
        מסננים{activeFilterCount > 0 ? ` (${activeFilterCount})` : ''}
      </button>
      {isFiltersOpen && (
        <section className={classes.scheduleFiltersPanel} aria-label="מסננים">
          <div className={classes.scheduleFiltersPanelHeader}>
            <h3>מסננים</h3>
            <button
              type="button"
              className="modalClose"
              aria-label="סגירת מסננים"
              onClick={() => setIsFiltersOpen(false)}
            >
              ×
            </button>
          </div>
          <div className={`${classes.scheduleFilterGroup} ${classes.days}`}>
            <h4>ימים</h4>
            <div className={classes.scheduleFilterChips}>
              {WEEKDAY_FILTER_LABELS.map((label, offset) => (
                <button
                  key={label}
                  type="button"
                  aria-pressed={selectedDayOffsets.has(offset)}
                  onClick={() => toggleSetValue(setSelectedDayOffsets, offset)}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
          <div className={`${classes.scheduleFilterGroup} ${classes.shifts}`}>
            <h4>משמרות</h4>
            <div className={classes.scheduleFilterChips}>
              {shiftFilterOptions.map((shiftName) => (
                <button
                  key={shiftName}
                  type="button"
                  aria-pressed={selectedShiftNames.has(shiftName)}
                  onClick={() => toggleSetValue(setSelectedShiftNames, shiftName)}
                >
                  {shiftName}
                </button>
              ))}
            </div>
          </div>
          <div className={`${classes.scheduleFilterGroup} ${classes.roles}`}>
            <h4>תפקידים</h4>
            <div className={classes.scheduleFilterChips}>
              {roleFilterOptions.map((roleName) => (
                <button
                  key={roleName}
                  type="button"
                  aria-pressed={selectedRoleNames.has(roleName)}
                  onClick={() => toggleSetValue(setSelectedRoleNames, roleName)}
                >
                  {roleName}
                </button>
              ))}
            </div>
          </div>
          <div className={classes.scheduleFiltersPanelActions}>
            <button type="button" onClick={onSelectAllFilters}>
              בחר הכל
            </button>
            <button type="button" onClick={onResetFilters}>
              נקה הכל
            </button>
          </div>
        </section>
      )}
    </div>

    <div className={classes.scheduleOptionsWrap}>
      <button
        type="button"
        className={classes.scheduleOptionsButton}
        onClick={() => setIsOptionsOpen((current) => !current)}
      >
        אפשרויות
      </button>
      {isOptionsOpen && (
        <div className={classes.scheduleOptionsMenu}>
          <button
            type="button"
            onClick={() => {
              setIsOptionsOpen(false);
              void refreshCurrentWeek();
            }}
          >
            רענון הסידור
          </button>
          <button
            type="button"
            onClick={() => {
              setIsOptionsOpen(false);
              setWeekStartDate(getCurrentWeekStartIsoDate());
            }}
          >
            מעבר לשבוע הנוכחי
          </button>
          <button
            type="button"
            onClick={() => {
              void onExportSchedule();
            }}
            disabled={!scheduleWeek}
          >
            ייצוא לאקסל
          </button>
        </div>
      )}
    </div>

    {user?.role === 'manager' && (
      <section className={classes.scheduleEmployeeList} aria-labelledby="schedule-employee-list-title">
        <div className={classes.scheduleEmployeeListHeader}>
          <h2 id="schedule-employee-list-title">עובדים בסידור</h2>
          <span>{employeeInsightSummaries.size}</span>
        </div>
        {employeeInsightSummaries.size === 0 ? (
          <p className={classes.scheduleEmployeeListEmpty}>אין עובדים משובצים בשבוע זה.</p>
        ) : (
          <div className={classes.scheduleEmployeeListItems}>
            {Array.from(employeeInsightSummaries.values()).map((summary) => {
              const warningText = summary.weeklyLimits ? getEmployeeLimitWarnings(summary.weeklyLimits.limits,summary.weeklyLimits.counts)
                .map(warning => `${EMPLOYEE_LIMIT_LABELS[warning.key]}: ${warning.actual} / ${warning.limit}`).join(' · ') : '';
              return (
              <button
                type="button"
                className={`${classes.scheduleEmployeeListItem}${employeeInsight?.employee.id === summary.employeePk ? ' active' : ''}`}
                key={summary.employeePk}
                onClick={() => {
                  void onOpenEmployeeInsight(summary.employeePk);
                }}
                disabled={isLoadingEmployeeInsight}
                aria-label={`פרטי עובד ${summary.employeeName}; ${summary.hasWeeklyNote ? 'יש הערה שבועית' : 'אין הערה שבועית'}; ${summary.requestedDays} ימים ביקש, ${summary.receivedDays} ימים קיבל${warningText ? `; ${warningText}` : ''}`}
              >
                <span
                  className={`${classes.employeeInsightButton} ${classes.static} ${summary.hasWeeklyNote ? classes.hasNote : classes.noNote}`}
                >
                  i
                </span>
                <span className={classes.scheduleEmployeeListIdentity}>
                  <strong>{summary.employeeName}</strong>
                  <small>
                    {summary.requestedDays} ביקש · {summary.receivedDays} קיבל
                  </small>
                  {warningText && <small className={classes.employeeLimitHint}>{warningText}</small>}
                </span>
              </button>
              );
            })}
          </div>
        )}
      </section>
    )}
  </aside>
);
